<template>
  <Accordion
    v-bind="$attrs"
    v-model:activeIndex="localActiveIndex"
    @update:active-index="onTabChange"
    class="page-accordion"
  >
    <component v-for="(tab, index) in tabs" :key="index" :is="AccordionTab" :pt="getPT(tab, index)">
      <template #headericon="{ isTabActive }">
        <template v-if="!loading">
          <span
            v-if="isTabActive"
            class="material-symbols-rounded mr-(--space-16) text-[var(--color-text-primary)]"
            aria-hidden="true"
            >expand_more</span
          >
          <span
            v-else
            class="material-symbols-rounded mr-(--space-16) text-[var(--color-text-primary)]"
            aria-hidden="true"
            >chevron_right</span
          >
        </template>

        <template v-else>
          <Skeleton width="18px" class="mr-(--space-16)" />
        </template>
      </template>

      <template #header>
        <div class="flex justify-between items-center w-full">
          <div role="group" class="flex flex-col" :aria-labelledby="`tab-header-title-${index}`">
            <div class="flex">
              <h2
                :id="`tab-header-title-${index}`"
                class="text-heading-lg text-[var(--color-text-primary)]"
                v-if="!loading"
              >
                {{ tab.header }}
              </h2>
              <Skeleton width="10rem" v-else />
            </div>

            <div class="flex" v-if="tab?.headerSubtitle">
              <span
                class="text-[var(--color-text-secondary)] font-normal text-sm mt-1"
                v-if="!loading"
              >
                {{ tab?.headerSubtitle }}</span
              >
              <Skeleton width="4rem" class="mt-1" v-else />
            </div>
          </div>

          <div class="flex items-center" v-if="tab?.actionsComponent">
            <div class="flex items-center">
              <component :is="tab.actionsComponent" v-bind="tab.actionsProps" v-if="!loading" />
              <Skeleton width="4rem" height="2rem" v-else />
            </div>
          </div>
        </div>
      </template>

      <div class="w-full">
        <div v-if="!loading">
          <component :is="tab.component" v-bind="tab.props" />
        </div>

        <div class="flex w-full" v-else>
          <ContentSkeleton />
        </div>
      </div>
    </component>
  </Accordion>
</template>

<script setup lang="ts">
import { ref, watch, type PropType } from 'vue';
import type { PageContent } from '@centric-os/types';
import { Accordion, AccordionTab, Skeleton } from '../../../core';
import ContentSkeleton from './components/content-skeleton/ContentSkeleton.vue';

// Define emits
const emit = defineEmits(['update:activeIndex']);

// Define options
defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  activeIndex: {
    type: [Number, Array] as PropType<number | number[]>,
  },
  tabs: {
    type: Array as PropType<PageContent[]>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

// Local state for activeIndex
const localActiveIndex = ref<number | number[]>(props.activeIndex);

// Update local value on tab change
const onTabChange = (index: number | number[]): void => {
  localActiveIndex.value = index;
};

const getPT = (tab: PageContent, index: number): Record<string, unknown> => {
  const root = {
    style: tab?.hidden ? 'display: none;' : '',
  };

  if (!tab?.header) {
    return { root };
  }

  return {
    root,
    toggleableContent: { 'aria-labelledby': `tab-header-title-${index}` },
  };
};

// Watch for changes in the prop and update the local value
watch(
  () => props.activeIndex,
  (newVal) => {
    localActiveIndex.value = newVal;
  },
);

// Emit changes back to the parent when activeIndex changes
watch(localActiveIndex, (newVal) => {
  emit('update:activeIndex', newVal);
});
</script>
